import React from 'react';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw } from "lucide-react";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, retryCount: 0 };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    
    // Log error for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  handleReset = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { fallback: Fallback } = this.props;
      
      // If custom fallback provided, use it
      if (Fallback) {
        return <Fallback error={this.state.error} retry={this.handleRetry} />;
      }

      // Default error UI
      return (
        <div className="min-h-[200px] flex items-center justify-center p-4">
          <Alert className="max-w-md" role="alert" aria-live="assertive">
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
            <AlertTitle>Something hiccuped</AlertTitle>
            <AlertDescription className="space-y-3">
              <p>This is usually a temporary blip. Try again — if it keeps happening, a reset should clear it.</p>
              {this.state.retryCount < 3 && (
                <Button
                  onClick={this.handleRetry}
                  variant="outline"
                  size="sm"
                  className="w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-foreground/30"
                >
                  <RefreshCw className="h-3 w-3 mr-2" aria-hidden="true" />
                  Try again
                </Button>
              )}
              {this.state.retryCount >= 3 && (
                <Button
                  onClick={this.handleReset}
                  variant="destructive"
                  size="sm"
                  className="w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-foreground/30"
                >
                  Reset the app
                </Button>
              )}
            </AlertDescription>
          </Alert>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;